import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { Hero, Statement, EmployerProof, HelpSection, ToolsBand, HowItWorks, StrategyBand, ReviewsSection, TeamSection, MathSection, FaqSection, FinalCta, Intro } from "./sections";
import { cityContent, EDMONTON_NEIGHBOURHOODS, regionOf, REGIONS } from "./cities";
import ComingSoonPage from "./ComingSoonPage";
import { SmartLink } from "./SiteHeader";
import { PHONE, EMAIL, ALBERTA_CITIES } from "./nav";
import { getPublic } from "./publicFetch";

const money = (v) => `$${Math.round(v).toLocaleString("en-CA")}`;
const yoy = (v) => `${v > 0 ? "+" : ""}${v}% Y/Y`;
const staticCity = (slug) => ({ slug, name: ALBERTA_CITIES.find(([, h]) => h === `/${slug}/`)?.[0] || slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) });

// Local market snapshot — AREA public statistics (approved addition, placed after "How it works").
export function MarketSnapshot({ snap, cityName, slug }) {
  if (!snap) return null;
  const t = snap.monthly.total;
  const lastYear = snap.period_label.replace(/(\d{4})$/, (y) => String(Number(y) - 1));
  const dir = (v, up, down, flat) => (v === 0 ? flat : `${yoy(v)} — ${v > 0 ? up : down}`);
  const stats = [
    ["Average price", money(t.avg_price), `${yoy(t.avg_price_yoy).replace(" Y/Y", "")} vs. ${lastYear}`],
    ["Homes sold", t.sales.toLocaleString("en-CA"), dir(t.sales_yoy, "more buyers in the market", "fewer buyers competing", "steady year-over-year")],
    ["New listings", t.new_listings.toLocaleString("en-CA"), dir(t.new_listings_yoy, "more fresh options", "fewer fresh options", "steady year-over-year")],
    ["Active inventory", t.inventory.toLocaleString("en-CA"), dir(t.inventory_yoy, "more choice than last year", "less choice than last year", "same choice as last year")],
    ["Months of supply", t.months_of_supply.toFixed(2), t.months_of_supply < 3 ? "Seller’s range (under 3)" : t.months_of_supply > 6 ? "Buyer’s range (over 6)" : "Balanced range (3–6)"],
    ["Sales-to-new-listings", `${t.snlr}%`, t.snlr > 60 ? "Seller’s range (over 60%)" : t.snlr < 40 ? "Buyer’s range (under 40%)" : "Balanced range (40–60%)"],
  ];
  const types = [["Detached", "detached"], ["Semi-detached", "semi_detached"], ["Row", "row"], ["Apartment", "apartment"]].filter(([, k]) => snap.monthly[k]);
  const byType = snap.buying?.by_type || {};
  const read = snap.read;
  return (
    <section className={`snapshot${read ? ` verdict-${read.verdict}` : ""}`} aria-labelledby="snapshot-heading" data-testid="market-snapshot">
      <div className="wrap">
        <div className="intro snap-intro">
          <div>
            <div className="eyebrow" data-testid="snapshot-eyebrow">{snap.is_proxy ? `${snap.scope_label} · ` : `${cityName} market · `}{snap.period_label}</div>
            <h2 className="display" id="snapshot-heading">{snap.is_proxy ? (snap.area_key === "alberta" ? "What the Alberta market is doing." : "What the nearest reported market is doing.") : "What the local market is doing."}</h2>
            {read && <span className="verdict-pill" data-testid="market-verdict">{read.label}</span>}
          </div>
          <p data-testid="market-takeaway">
            {read ? read.takeaway : "Real resale numbers, not vibes."}
            {snap.is_proxy && <> <small className="snap-proxy" data-testid="snapshot-proxy-note">AREA doesn’t publish a {cityName}-specific report, so these are {snap.scope_label} figures — {cityName} prices and conditions can differ. Ask us for a street-level read.</small></>}
          </p>
        </div>
        <div className="snapshot-grid">
          {stats.map(([l, v, s]) => <div className="snap-stat" key={l}><span className="snap-label">{l}</span><strong>{v}</strong><small>{s}</small></div>)}
        </div>
        {types.length > 0 && <TypeTable types={types} monthly={snap.monthly} byType={byType} />}
        <p className="snap-source">Source: {snap.source_label}, {snap.scope_label}, {snap.period_label}. Residential resale activity; figures as published by the Alberta Real Estate Association.{snap.buying && <> Payments at {snap.buying.rate.toFixed(2)}% over {snap.buying.amort_years} years (CMHC premium added to the mortgage when under 20% down); income at the {snap.buying.qualifying_rate.toFixed(2)}% stress-test rate.</>}</p>
        {snap.buying && <BuyingPower b={snap.buying} cityName={cityName} slug={slug} proxyLabel={snap.is_proxy ? snap.scope_label.replace(" data", "") : null} />}
      </div>
    </section>
  );
}

function TypeTable({ types, monthly, byType }) {
  const [mode, setMode] = useState("minimum");
  return (
    <div className="snap-table-wrap">
      <div className="snap-table-bar">
        <span className="snap-label">By property type</span>
        <div className="snap-toggle" role="group" aria-label="Down payment scenario" data-testid="down-toggle">
          {[["minimum", "Minimum down"], ["conventional", "20% down"]].map(([k, l]) => (
            <button type="button" key={k} className={mode === k ? "on" : ""} aria-pressed={mode === k} onClick={() => setMode(k)} data-testid={`down-toggle-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <table className="snap-table" data-testid="type-table"><caption className="sr-only">Average price, months of supply, monthly payment and income to qualify by property type</caption>
        <thead><tr><th scope="col">Property type</th><th scope="col">Average price</th><th scope="col">Months of supply</th><th scope="col">Down payment</th><th scope="col">Monthly payment</th><th scope="col">Income to qualify</th></tr></thead>
        <tbody>{types.map(([label, k]) => { const r = monthly[k]; const b = byType[k]?.[mode]; return (
          <tr key={k} data-testid={`type-row-${k}`}>
            <th scope="row">{label}</th>
            <td data-label="Average price">{money(r.avg_price)}</td>
            <td data-label="Months of supply">{r.months_of_supply.toFixed(2)}<span className="unit">{r.months_of_supply < 3 ? "fast" : r.months_of_supply > 6 ? "slow" : "steady"}</span></td>
            <td data-label="Down payment">{b ? <>{money(b.down)}<span className="unit">{b.down_pct}%</span></> : "—"}</td>
            <td className="hl" data-label="Monthly payment" data-testid={`type-payment-${k}`}>{b ? <>{money(b.monthly_payment)}<span className="unit">/mo</span></> : "—"}</td>
            <td data-label="Income to qualify" data-testid={`type-income-${k}`}>{b ? <>~{money(b.income_needed)}<span className="unit">/yr</span></> : "—"}</td>
          </tr>); })}</tbody>
      </table>
    </div>
  );
}

function BuyingPower({ b, cityName, slug, proxyLabel }) {
  const labels = { minimum: ["Minimum down", "Insured · CMHC premium added to the mortgage"], conventional: ["20% down", "Conventional · no default insurance, widest lender choice"] };
  return (
    <div className="snap-buy" data-testid="buying-power">
      <div className="snap-buy-intro">
        <span className="eyebrow" data-testid="buying-eyebrow">{proxyLabel ? `What it takes to buy at the ${proxyLabel} average` : `What it takes to buy in ${cityName}`}</span>
        <h3 className="display">Turning the average price into a monthly number.</h3>
        <p>{proxyLabel
          ? <>AREA doesn’t publish a {cityName} average, so we’ve used the {proxyLabel} figure of {money(b.price)}. {cityName} prices often run differently — treat these as a starting point, not a quote.</>
          : <>At {cityName}’s {money(b.price)} average, here’s the down payment, the monthly payment and the household income the stress test wants to see.</>}</p>
      </div>
      <div className="snap-buy-grid">
        {b.scenarios.map((s) => (
          <div className="snap-buy-card" key={s.key} data-testid={`buy-card-${s.key}`}>
            <span className="snap-label">{labels[s.key][0]} — {s.down_pct}%</span>
            <strong className="snap-buy-down">{money(s.down)}</strong>
            <small>{labels[s.key][1]}</small>
            <dl className="snap-buy-rows">
              {s.cmhc_premium > 0 && <><dt>CMHC premium</dt><dd>{money(s.cmhc_premium)}</dd></>}
              <dt>Mortgage amount</dt><dd>{money(s.mortgage)}</dd>
              <dt>Monthly payment</dt><dd className="hl" data-testid={`buy-payment-${s.key}`}>{money(s.monthly_payment)}<span className="unit">/mo</span></dd>
              <dt>Income to qualify</dt><dd className="hl" data-testid={`buy-income-${s.key}`}>~{money(s.income_needed)}<span className="unit">/yr</span></dd>
            </dl>
          </div>
        ))}
      </div>
      <p className="snap-buy-fine" data-testid="buying-assumptions">
        {b.is_live
          ? <>Based on today’s <strong>{b.rate.toFixed(2)}% 5-year fixed</strong> across {b.lender_count || "50+"} lenders</>
          : <>Based on a <strong>representative {b.rate.toFixed(2)}% 5-year fixed</strong> — <SmartLink href={`/${slug}/mortgage-rates/`} className="textlink">see today’s rates</SmartLink> for the live number</>},
        {" "}{b.amort_years}-year amortization, principal and interest only. Income to qualify uses the {b.qualifying_rate.toFixed(2)}% stress-test rate, 39% GDS, property tax at {b.property_tax_pct}% of price and ${b.monthly_heat} heat. Your file will differ — that’s the point of the conversation.
      </p>
      <div className="snap-buy-links">
        <SmartLink href="/calculators/affordability/" className="btn outline" data-testid="buying-affordability-link">Run your own numbers</SmartLink>
        <SmartLink href={`/${slug}/mortgage-rates/`} className="textlink" data-testid="buying-rates-link">Today’s {cityName} rates →</SmartLink>
      </div>
    </div>
  );
}

export default function MunicipalPage() {
  const slug = useLocation().pathname.split("/").filter(Boolean)[0];
  const [state, setState] = useState({ city: undefined, snap: null, all: [] });

  useEffect(() => {
    let live = true;
    (async () => {
      const [cityRes, snapRes, allRes] = await Promise.all([getPublic(`/municipalities/${slug}`), getPublic(`/market-snapshot/${slug}`, { retries: 0 }), getPublic("/municipalities", { retries: 0 })]);
      // Known city route whose API call failed (not a 404) → render from static copy rather than a noindex holding page.
      const city = cityRes.data || (cityRes.error ? staticCity(slug) : null);
      if (live) setState({ city, snap: snapRes.data, all: allRes.data || [] });
    })().catch(() => live && setState({ city: staticCity(slug), snap: null, all: [] }));
    return () => { live = false; };
  }, [slug]);

  const { city, snap, all } = state;
  if (city === undefined) return <SiteLayout loading><section className="hero"><div className="wrap"><div className="eyebrow">Loading…</div></div></section></SiteLayout>;
  if (city === null) return <ComingSoonPage />;

  const c = cityContent(city);
  const region = regionOf(slug);
  const places = (region ? REGIONS[region] : []).filter((s) => s !== slug).map((s) => all.find((m) => m.slug === s)).filter(Boolean).map((m) => [`${m.name} mortgage broker`, `/${m.slug}/`]);
  const faqs = [...c.faqs, ...((snap?.faqs || []).map((f) => [f.q, f.a]))];
  const ld = [
    { "@context": "https://schema.org", "@type": "FinancialService", name: "Hello Mortgage Team", url: `https://www.hellomortgage.ca/${slug}/`, telephone: `+1-${PHONE}`, email: EMAIL,
      areaServed: slug === "edmonton"
        ? [{ "@type": "City", name: "Edmonton" }, ...EDMONTON_NEIGHBOURHOODS.map((n) => ({ "@type": "Place", name: n, containedInPlace: { "@type": "City", name: "Edmonton" } }))]
        : { "@type": "City", name: city.name }, parentOrganization: { "@type": "Organization", name: "BRX Mortgage" },
      description: `${city.name} mortgage brokerage services for home purchases, pre-approvals, renewals, refinancing and mortgage planning.` },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  ];

  return (
    <SiteLayout directory={{ ...c.directory, places: places.length ? places : c.directory.services.slice(0, 4) }}>
      <SeoHead title={c.title} description={c.description} path={`/${slug}/`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <Hero eyebrow={c.hero.eyebrow} text={c.hero.text} testId="city-hero" />
      <Statement />
      <EmployerProof region={slug === region ? region : null} cityName={city.name} />
      <HelpSection eyebrow={c.help.eyebrow} text={c.help.text} intents={c.help.intents} />
      <ToolsBand eyebrow={c.tools.eyebrow} text={c.tools.text} />
      <HowItWorks eyebrow={c.how.eyebrow} text={c.how.text} />
      <MarketSnapshot snap={snap} cityName={city.name} slug={slug} />
      <StrategyBand text={c.strategy} heading={c.strategyHeading} />
      <ReviewsSection city={city.name} region={(region ? REGIONS[region] : []).map((s) => all.find((m) => m.slug === s)?.name).filter(Boolean)} />
      <TeamSection paragraphs={c.team} />
      <MathSection text={c.math.text} fine={c.math.fine} cta={c.math.cta} ratesHref={`/${slug}/mortgage-rates/`} />
      <FaqSection items={faqs} heading={c.faq_heading} />
      <FinalCta text={c.final} />
    </SiteLayout>
  );
}
